import { Text } from '@thanx/uikit/text'
import { getSampleCustomers } from 'actions/sampleCustomers'
import InfiniteScrollContainer from 'components/InfiniteScrollContainer'
import { SEGMENT_DETAILS } from 'components/SegmentDetails/constants'
import SampleCustomersContent from 'components/SegmentDetails/SampleCustomersContent'
import { buildTranslate } from 'locales'
import { useCallback, useEffect, useState } from 'react'
import Tab from 'react-bootstrap-five/Tab'
import { useSelector } from 'react-redux'
import { selectPagination } from 'selectors/pagination'
import { errorHandler } from 'utilities/errorHandler'

const t = buildTranslate('segments.details')

interface SampleCustomersProps {
  segmentId: string
  tabId?: string
}

const SampleCustomers = ({ segmentId, tabId }: SampleCustomersProps) => {
  const pagination = useSelector(state =>
    selectPagination(state, 'sample_customers', segmentId)
  )
  const customerCount = pagination?.customer_count
  // InfiniteScrollContainer has no error state: a failed first page leaves its
  // spinner up for good, so swap it for a message. Keyed to the segment so a
  // previous segment's failure never shows here, and cleared on change so
  // returning to a failed segment retries.
  const [failedSegmentId, setFailedSegmentId] = useState<string | null>(null)
  useEffect(() => setFailedSegmentId(null), [segmentId])
  const showLoadError = failedSegmentId === segmentId && !pagination
  // useInfiniteScroll refetches page 1 whenever these change before it loads,
  // so inline callbacks re-sent the request on every parent re-render.
  const fetchAction = useCallback(
    (params: Parameters<typeof getSampleCustomers>[1]) =>
      getSampleCustomers(segmentId, params),
    [segmentId]
  )
  const handleError = useCallback(
    (error?: { status?: number; response?: { status?: number } }) => {
      errorHandler.capture('Failed to load sample customers', {
        segmentId,
        status: error?.response?.status ?? error?.status,
      })
      setFailedSegmentId(segmentId)
    },
    [segmentId]
  )

  return (
    <Tab.Pane
      eventKey={SEGMENT_DETAILS.TAB_KEYS.SAMPLE_CUSTOMERS}
      className="segment-details__tab-pane"
      role="tabpanel"
      id={
        tabId
          ? `${tabId}-${SEGMENT_DETAILS.TAB_KEYS.SAMPLE_CUSTOMERS}-panel`
          : undefined
      }
      aria-labelledby={
        tabId
          ? `${tabId}-${SEGMENT_DETAILS.TAB_KEYS.SAMPLE_CUSTOMERS}-tab`
          : undefined
      }
    >
      {showLoadError ? (
        <div role="alert">
          <Text.BodyText4 color="grey70">
            {t('sample_customers.load_error')}
          </Text.BodyText4>
        </div>
      ) : (
        <InfiniteScrollContainer
          paginationId={segmentId}
          paginationType="sample_customers"
          fetchAction={fetchAction}
          emptyText={t('sample_customers.empty_state')}
          initialLoadingText={t('sample_customers.loading_message')}
          loadMoreText={t('sample_customers.load_more')}
          onError={handleError}
        >
          {props => (
            <SampleCustomersContent {...props} customerCount={customerCount} />
          )}
        </InfiniteScrollContainer>
      )}
    </Tab.Pane>
  )
}

export default SampleCustomers
